import { useState, useCallback, useRef, useEffect } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { getAccessToken } from '@/lib/auth';

// 2-Container Architecture: Backend serves all endpoints at /api/v1
// PROJ-059: Canvas uses "Flujos Visuales" (visual-flows) API
const API_BASE = '/api/v1';
const VISUAL_FLOWS_ENDPOINT = `${API_BASE}/visual-flows`;

// Helper to build headers with authentication
function getAuthHeaders(includeContentType = true): HeadersInit {
  const headers: HeadersInit = {};
  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

export interface WorkflowMetadata {
  code?: string;
  name: string;
  description?: string;
  status?: 'draft' | 'active' | 'archived';
  visibility?: 'private' | 'team' | 'organization';
  tags?: string[];
  teamId?: string;
  businessUnitId?: string;
  ownerId?: string;
}

// Viewport type for persisting zoom and pan position
export interface WorkflowViewport {
  x: number;
  y: number;
  zoom: number;
}

// Default viewport: 90% zoom, centered
export const DEFAULT_VIEWPORT: WorkflowViewport = { x: 0, y: 0, zoom: 0.9 };

export interface Workflow {
  id: string;
  code: string;
  name: string;
  description?: string;
  version: number;
  status: string;
  visibility: string;
  tags: Array<{
    id: string;
    code: string;
    name: string;
    color?: string;
    icon?: string;
  }>;
  metadata?: Record<string, any>;
  viewport?: WorkflowViewport; // Persisted viewport (zoom, pan)
  nodeCount: number;
  edgeCount: number;
  nodes: Node[];
  edges: Edge[];
  owner?: {
    id: string;
    name: string;
    email: string;
  };
  team?: {
    id: string;
    name: string;
    code: string;
  };
  businessUnit?: {
    id: string;
    name: string;
    code: string;
  };
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
}

export interface WorkflowListItem {
  id: string;
  code: string;
  name: string;
  description?: string;
  version: number;
  status: string;
  visibility: string;
  tags: Array<{
    id: string;
    code: string;
    name: string;
    color?: string;
    icon?: string;
  }>;
  nodeCount: number;
  edgeCount: number;
  owner?: {
    id: string;
    name?: string;
    fullName?: string;
    username?: string;
  };
  team?: {
    id: string;
    name: string;
  };
  businessUnit?: {
    id: string;
    name: string;
  };
  updatedAt?: string;
  isActive?: boolean;
}

interface UseWorkflowOptions {
  autoSaveInterval?: number; // milliseconds, 0 to disable
  onSaveSuccess?: (workflow: Workflow) => void;
  onSaveError?: (error: Error) => void;
  onLoadSuccess?: (workflow: Workflow) => void;
  onLoadError?: (error: Error) => void;
}

export function useWorkflow(options: UseWorkflowOptions = {}) {
  const {
    autoSaveInterval = 30000, // 30 seconds default
    onSaveSuccess,
    onSaveError,
    onLoadSuccess,
    onLoadError,
  } = options;

  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef<number | null>(null);

  /**
   * Create a new workflow
   */
  const createWorkflow = useCallback(
    async (metadata: WorkflowMetadata, nodes: Node[], edges: Edge[], viewport?: WorkflowViewport): Promise<Workflow | null> => {
      setIsSaving(true);
      try {
        const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            ...metadata,
            viewport: viewport || DEFAULT_VIEWPORT,
            nodes: nodes.map(node => ({
              id: node.id,
              type: node.type,
              label: node.data?.label || '',
              position: node.position,
              data: node.data,
              parentId: node.parentId,
            })),
            edges: edges.map(edge => ({
              id: edge.id,
              source: edge.source,
              target: edge.target,
              type: edge.type,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
              animated: edge.animated,
              label: edge.label,
              data: edge.data,
            })),
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Failed to create workflow');
        }

        const result = await response.json();
        const workflow = result.data;

        setCurrentWorkflow(workflow);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);

        onSaveSuccess?.(workflow);
        return workflow;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        onSaveError?.(err);
        console.error('Error creating workflow:', err);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [onSaveSuccess, onSaveError]
  );

  /**
   * Update existing workflow
   */
  const updateWorkflow = useCallback(
    async (workflowId: string, metadata: Partial<WorkflowMetadata>, nodes: Node[], edges: Edge[], viewport?: WorkflowViewport): Promise<Workflow | null> => {
      setIsSaving(true);
      try {
        const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/${workflowId}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            ...metadata,
            viewport: viewport,
            nodes: nodes.map(node => ({
              id: node.id,
              type: node.type,
              label: node.data?.label || '',
              position: node.position,
              data: node.data,
              parentId: node.parentId,
            })),
            edges: edges.map(edge => ({
              id: edge.id,
              source: edge.source,
              target: edge.target,
              type: edge.type,
              sourceHandle: edge.sourceHandle,
              targetHandle: edge.targetHandle,
              animated: edge.animated,
              label: edge.label,
              data: edge.data,
            })),
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Failed to update workflow');
        }

        const result = await response.json();
        const workflow = result.data;

        setCurrentWorkflow(workflow);
        setLastSaved(new Date());
        setHasUnsavedChanges(false);

        onSaveSuccess?.(workflow);
        return workflow;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        onSaveError?.(err);
        console.error('Error updating workflow:', err);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [onSaveSuccess, onSaveError]
  );

  /**
   * Save workflow (create or update based on currentWorkflow)
   */
  const saveWorkflow = useCallback(
    async (metadata: WorkflowMetadata | Partial<WorkflowMetadata>, nodes: Node[], edges: Edge[], viewport?: WorkflowViewport): Promise<Workflow | null> => {
      if (currentWorkflow) {
        return updateWorkflow(currentWorkflow.id, metadata, nodes, edges, viewport);
      } else {
        return createWorkflow(metadata as WorkflowMetadata, nodes, edges, viewport);
      }
    },
    [currentWorkflow, createWorkflow, updateWorkflow]
  );

  /**
   * Load workflow by ID
   */
  const loadWorkflow = useCallback(
    async (workflowId: string): Promise<Workflow | null> => {
      setIsLoading(true);
      try {
        const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/${workflowId}`, {
          headers: getAuthHeaders(false),
          credentials: 'include',
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error?.message || 'Failed to load workflow');
        }

        const result = await response.json();
        const workflow = result.data;

        setCurrentWorkflow(workflow);
        setHasUnsavedChanges(false);
        setLastSaved(new Date(workflow.updatedAt || workflow.createdAt));

        onLoadSuccess?.(workflow);
        return workflow;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        onLoadError?.(err);
        console.error('Error loading workflow:', err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [onLoadSuccess, onLoadError]
  );

  /**
   * List workflows with pagination
   */
  const listWorkflows = useCallback(
    async (options: {
      page?: number;
      limit?: number;
      status?: string;
      q?: string;
    } = {}): Promise<{ workflows: WorkflowListItem[]; total: number; page: number; totalPages: number } | null> => {
      try {
        const params = new URLSearchParams();
        if (options.page) params.set('page', options.page.toString());
        if (options.limit) params.set('limit', options.limit.toString());
        if (options.status) params.set('status', options.status);
        if (options.q) params.set('q', options.q);

        const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}?${params}`, {
          headers: getAuthHeaders(false),
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to list workflows');
        }

        const result = await response.json();

        return {
          workflows: result.data,
          total: result.meta?.pagination?.total ?? result.meta?.total ?? 0,
          page: result.meta?.pagination?.page ?? result.meta?.page ?? 1,
          totalPages: result.meta?.pagination?.totalPages ?? result.meta?.totalPages ?? 1,
        };
      } catch (error) {
        console.error('Error listing workflows:', error);
        return null;
      }
    },
    []
  );

  /**
   * Delete workflow
   */
  const deleteWorkflow = useCallback(async (workflowId: string): Promise<boolean> => {
    try {
      const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/${workflowId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(false),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete workflow');
      }

      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow(null);
        setHasUnsavedChanges(false);
      }

      return true;
    } catch (error) {
      console.error('Error deleting workflow:', error);
      return false;
    }
  }, [currentWorkflow]);

  /**
   * Duplicate workflow
   */
  const duplicateWorkflow = useCallback(async (workflowId: string): Promise<Workflow | null> => {
    try {
      const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/${workflowId}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders(false),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate workflow');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      return null;
    }
  }, []);

  /**
   * Export workflow to JSON
   */
  const exportWorkflow = useCallback(async (workflowId: string): Promise<void> => {
    try {
      const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/${workflowId}/export`, {
        headers: getAuthHeaders(false),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to export workflow');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workflow-${workflowId}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting workflow:', error);
    }
  }, []);

  /**
   * Import workflow from JSON
   */
  const importWorkflow = useCallback(
    async (jsonData: any): Promise<Workflow | null> => {
      try {
        const response = await fetch(`${VISUAL_FLOWS_ENDPOINT}/import`, {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(jsonData),
        });

        if (!response.ok) {
          throw new Error('Failed to import workflow');
        }

        const result = await response.json();
        const workflow = result.data;

        setCurrentWorkflow(workflow);
        onLoadSuccess?.(workflow);

        return workflow;
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        onLoadError?.(err);
        console.error('Error importing workflow:', error);
        return null;
      }
    },
    [onLoadSuccess, onLoadError]
  );

  /**
   * Mark workflow as having unsaved changes
   */
  const markAsChanged = useCallback(() => {
    setHasUnsavedChanges(true);
  }, []);

  /**
   * Reset current workflow
   */
  const resetWorkflow = useCallback(() => {
    setCurrentWorkflow(null);
    setHasUnsavedChanges(false);
    setLastSaved(null);
  }, []);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, []);

  /**
   * Setup auto-save (call this from useEffect in the component)
   */
  const setupAutoSave = useCallback(
    (nodes: Node[], edges: Edge[], viewport?: WorkflowViewport) => {
      if (!autoSaveInterval || !currentWorkflow || !hasUnsavedChanges) {
        return;
      }

      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set new timer
      autoSaveTimerRef.current = setTimeout(() => {
        updateWorkflow(currentWorkflow.id, {}, nodes, edges, viewport);
      }, autoSaveInterval);

      // Cleanup
      return () => {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
      };
    },
    [autoSaveInterval, currentWorkflow, hasUnsavedChanges, updateWorkflow]
  );

  return {
    // State
    currentWorkflow,
    isSaving,
    isLoading,
    lastSaved,
    hasUnsavedChanges,

    // Actions
    createWorkflow,
    updateWorkflow,
    saveWorkflow,
    loadWorkflow,
    listWorkflows,
    deleteWorkflow,
    duplicateWorkflow,
    exportWorkflow,
    importWorkflow,
    markAsChanged,
    resetWorkflow,
    setupAutoSave,
  };
}
